import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  IconButton,
  Chip,
  Divider,
  Alert,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  alpha,
} from '@mui/material';
import {
  Close as CloseIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  RestartAlt as ResetIcon,
} from '@mui/icons-material';
import { LocalizationProvider, TimePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { useSnackbar } from 'notistack';
import { apiGet, apiPost, apiDelete } from '../utils/api';

const ShiftOverrideModal = ({ 
  open, 
  onClose, 
  therapist, 
  date, 
  currentShift,
  shifts,
  onSave 
}) => {
  const { enqueueSnackbar } = useSnackbar();
  
  // Form state
  const [selectedShift, setSelectedShift] = useState('');
  const [startTime, setStartTime] = useState(null);
  const [endTime, setEndTime] = useState(null);
  const [breaks, setBreaks] = useState([]);
  const [notes, setNotes] = useState('');
  const [hasExistingOverride, setHasExistingOverride] = useState(false);
  const [loading, setLoading] = useState(false);
  
  // Track original shift defaults to detect changes
  const [originalDefaults, setOriginalDefaults] = useState({
    start_time: null,
    end_time: null,
    breaks: []
  });

  // Load shift data and existing override
  useEffect(() => {
    if (open && therapist && date) {
      if (currentShift) {
        setSelectedShift(currentShift.shift_code || '');
        loadShiftData(currentShift.shift_code || '', currentShift);
      }
    }
  }, [open, therapist, date, currentShift]);

  // Reload shift data when shift selection changes (manual change, not initial load)
  const handleShiftChange = async (newShiftCode) => {
    console.log('🔄 Shift changed to:', newShiftCode);
    setSelectedShift(newShiftCode);
    
    const shiftData = shifts.find(s => s.shift_code === newShiftCode);
    if (!shiftData) {
      console.error('❌ Shift data not found for:', newShiftCode);
      return;
    }
    
    console.log('📋 Shift data:', shiftData);
    
    // Check if there's an override for this therapist on this date with this shift
    if (therapist?.id && date) {
      try {
        const response = await apiGet(`/api/roster/overrides/${date}/${therapist.id}`);
        
        if (response.ok) {
          const override = await response.json();
          // If override exists AND it's for the same shift, use override values
          if (override.shift_code === newShiftCode) {
            setStartTime(dayjs(override.override_start_time || shiftData.start_time, 'HH:mm'));
            setEndTime(dayjs(override.override_end_time || shiftData.end_time, 'HH:mm'));
            setBreaks(override.override_breaks && override.override_breaks.length > 0 
              ? override.override_breaks 
              : (shiftData.breaks || [])
            );
            setNotes(override.notes || '');
            setHasExistingOverride(true);
          } else {
            // Different shift, use shift defaults
            setStartTime(dayjs(shiftData.start_time, 'HH:mm'));
            setEndTime(dayjs(shiftData.end_time, 'HH:mm'));
            setBreaks(shiftData.breaks || []);
            setNotes('');
            setHasExistingOverride(false);
          }
        } else {
          // No override, use shift defaults
          setStartTime(dayjs(shiftData.start_time, 'HH:mm'));
          setEndTime(dayjs(shiftData.end_time, 'HH:mm'));
          setBreaks(shiftData.breaks || []);
          setNotes('');
          setHasExistingOverride(false);
        }
      } catch (error) {
        console.error('Error checking override:', error);
        // Fallback to shift defaults
        setStartTime(dayjs(shiftData.start_time, 'HH:mm'));
        setEndTime(dayjs(shiftData.end_time, 'HH:mm'));
        setBreaks(shiftData.breaks || []);
        setNotes('');
        setHasExistingOverride(false);
      }
    } else {
      // No therapist/date, just use shift defaults
      console.log('⚙️ Using shift defaults:', {
        start: shiftData.start_time,
        end: shiftData.end_time,
        breaks: shiftData.breaks
      });
      setStartTime(dayjs(shiftData.start_time, 'HH:mm'));
      setEndTime(dayjs(shiftData.end_time, 'HH:mm'));
      setBreaks(shiftData.breaks || []);
      setNotes('');
      setHasExistingOverride(false);
    }
    
    // Update original defaults for change detection
    setOriginalDefaults({
      start_time: shiftData.start_time,
      end_time: shiftData.end_time,
      breaks: shiftData.breaks || []
    });
    
    console.log('✅ State updated - Start:', shiftData.start_time, 'End:', shiftData.end_time, 'Breaks:', shiftData.breaks?.length || 0);
  };

  const loadShiftData = async (shiftCode, shiftDetails) => {
    if (!therapist?.id || !date) return;
    
    // Store original shift defaults
    const defaults = {
      start_time: shiftDetails.start_time,
      end_time: shiftDetails.end_time,
      breaks: shiftDetails.breaks || []
    };
    setOriginalDefaults(defaults);
    
    // Try to load existing override
    try {
      const response = await apiGet(`/api/roster/overrides/${date}/${therapist.id}`);
      
      if (response.ok) {
        const override = await response.json();
        setHasExistingOverride(true);
        
        // Use override values if they exist, otherwise use shift defaults
        setStartTime(dayjs(override.override_start_time || shiftDetails.start_time, 'HH:mm'));
        setEndTime(dayjs(override.override_end_time || shiftDetails.end_time, 'HH:mm'));
        setBreaks(override.override_breaks && override.override_breaks.length > 0 
          ? override.override_breaks 
          : (shiftDetails.breaks || [])
        );
        setNotes(override.notes || '');
      } else {
        // No override, use shift defaults
        setHasExistingOverride(false);
        setStartTime(dayjs(shiftDetails.start_time, 'HH:mm'));
        setEndTime(dayjs(shiftDetails.end_time, 'HH:mm'));
        setBreaks(shiftDetails.breaks || []);
        setNotes('');
      }
    } catch (error) {
      console.error('Error loading override:', error);
      // Fallback to shift defaults
      setStartTime(dayjs(shiftDetails.start_time, 'HH:mm'));
      setEndTime(dayjs(shiftDetails.end_time, 'HH:mm'));
      setBreaks(shiftDetails.breaks || []);
    }
  };

  const handleAddBreak = () => {
    setBreaks([
      ...breaks,
      {
        start_time: '12:00',
        end_time: '13:00',
        break_type: 'BREAK',
        description: ''
      }
    ]);
  };

  const handleUpdateBreak = (index, field, value) => {
    const newBreaks = [...breaks];
    newBreaks[index] = {
      ...newBreaks[index],
      [field]: value
    };
    setBreaks(newBreaks);
  };

  const handleDeleteBreak = (index) => {
    setBreaks(breaks.filter((_, i) => i !== index));
  };

  // Detect if current values differ from shift defaults
  const hasChanges = () => {
    const startChanged = normalizeTimeLabel(startTime?.format('HH:mm')) !== normalizeTimeLabel(originalDefaults.start_time);
    const endChanged = normalizeTimeLabel(endTime?.format('HH:mm')) !== normalizeTimeLabel(originalDefaults.end_time);
    const breaksChanged = JSON.stringify(breaks) !== JSON.stringify(originalDefaults.breaks);
    return startChanged || endChanged || breaksChanged || notes.length > 0;
  };

  const normalizeTimeLabel = (value) => {
    if (!value) return '';
    const parsed = dayjs(value, ['HH:mm', 'H:mm', 'hh:mm A', 'h:mm A']);
    return parsed.isValid() ? parsed.format('HH:mm') : String(value);
  };

  const handleSave = async () => {
    if (!therapist?.id || !date || !selectedShift) {
      enqueueSnackbar('Please select a shift', { variant: 'error' });
      return;
    }

    setLoading(true);

    try {
      const startChanged = normalizeTimeLabel(startTime?.format('HH:mm')) !== normalizeTimeLabel(originalDefaults.start_time);
      const endChanged = normalizeTimeLabel(endTime?.format('HH:mm')) !== normalizeTimeLabel(originalDefaults.end_time);
      const breaksChanged = JSON.stringify(breaks) !== JSON.stringify(originalDefaults.breaks);
      const hasActualOverrides = startChanged || endChanged || breaksChanged || (notes && notes.trim().length > 0);
      
      const shiftChanged = currentShift && currentShift.shift_code !== selectedShift;
      
      console.log('Save decision:', {
        hasActualOverrides,
        shiftChanged,
        startChanged,
        endChanged,
        breaksChanged,
        hasNotes: notes && notes.trim().length > 0
      });

      if (shiftChanged) {
        const assignResponse = await apiPost('/api/shifts/assign', {
          date: date,
          shift_code: selectedShift,
          therapist_ids: [String(therapist.id)],
        });

        if (!assignResponse.ok) {
          const errorData = await assignResponse.json().catch(() => ({}));
          throw new Error(errorData.detail || 'Failed to update shift assignment');
        }
      }

      if (hasActualOverrides) {
        const payload = {
          date: date,
          therapist_id: therapist.id,
          shift_code: selectedShift,
          override_start_time: startTime ? startTime.format('HH:mm') : null,
          override_end_time: endTime ? endTime.format('HH:mm') : null,
          override_breaks: breaks.length > 0 ? breaks : null,
          notes: notes || '',
        };

        const response = await apiPost('/api/roster/overrides', payload);

        if (response.ok) {
          enqueueSnackbar('Custom schedule saved', { variant: 'success' });
          setHasExistingOverride(true);
          onSave && onSave();
          handleClose();
        } else {
          const error = await response.json().catch(() => ({}));
          throw new Error(error.detail || 'Failed to save override');
        }
      } else if (shiftChanged) {
        enqueueSnackbar('Shift assignment updated', { variant: 'success' });
        onSave && onSave();
        handleClose();
      } else {
        enqueueSnackbar('No changes to save', { variant: 'info' });
        handleClose();
      }
    } catch (error) {
      console.error('Error saving:', error);
      enqueueSnackbar(error.message || 'Failed to save', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!therapist?.id || !date) return;

    if (!window.confirm('Delete this override and revert to shift defaults?')) {
      return;
    }

    setLoading(true);

    try {
      const response = await apiDelete(`/api/roster/overrides/${date}/${therapist.id}`);

      if (response.ok) {
        enqueueSnackbar('Override deleted, reverted to defaults', { variant: 'success' });
        onSave && onSave();
        handleClose();
      } else if (response.status === 404) {
        enqueueSnackbar('No override to delete', { variant: 'info' });
        handleClose();
      } else {
        throw new Error('Failed to delete override');
      }
    } catch (error) {
      console.error('Error deleting override:', error);
      enqueueSnackbar('Failed to delete override', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setSelectedShift('');
    setStartTime(null);
    setEndTime(null);
    setBreaks([]);
    setNotes('');
    setHasExistingOverride(false);
    setOriginalDefaults({ start_time: null, end_time: null, breaks: [] });
    onClose();
  };

  const selectedShiftData = shifts.find(s => s.shift_code === selectedShift);

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Dialog 
        open={open} 
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            maxHeight: '90vh'
          }
        }}
      >
        <DialogTitle sx={{ 
          borderBottom: '1px solid',
          borderColor: 'divider',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          pb: 2
        }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Edit Shift Assignment
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {therapist?.name} • {dayjs(date).format('MMM DD, YYYY')}
            </Typography>
          </Box>
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ pt: 3 }}>
          {hasExistingOverride && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                ⚡ Custom Schedule (changes saved for this person only)
              </Typography>
            </Alert>
          )}

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* Shift Reference Table - Clickable to change shift */}
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1.5, color: 'text.secondary' }}>
                📋 All Shifts (click to select)
              </Typography>
              <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 200 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600, width: 80 }}>Shift</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                      <TableCell sx={{ fontWeight: 600, width: 120 }}>Time</TableCell>
                      <TableCell sx={{ fontWeight: 600, width: 100 }}>Breaks</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {shifts.map((shift) => (
                      <TableRow 
                        key={shift.shift_code}
                        onClick={() => handleShiftChange(shift.shift_code)}
                        sx={{
                          cursor: 'pointer',
                          bgcolor: shift.shift_code === selectedShift ? '#e3f2fd' : 'inherit',
                          '&:hover': { bgcolor: shift.shift_code === selectedShift ? '#e3f2fd' : '#f5f5f5' },
                          transition: 'background-color 0.2s'
                        }}
                      >
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box 
                              sx={{ 
                                width: 10, 
                                height: 10, 
                                borderRadius: '50%', 
                                bgcolor: shift.color || '#f0f0f0',
                                border: '1px solid #ddd'
                              }} 
                            />
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {shift.shift_code}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>{shift.name}</TableCell>
                        <TableCell>
                          <Typography variant="caption" sx={{ fontWeight: 500 }}>
                            {shift.start_time} - {shift.end_time}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption">
                            {shift.breaks?.length > 0 ? `${shift.breaks.length} break(s)` : '-'}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>

            <Divider />

            {/* Selected Shift - Pre-filled for editing */}
            <Box sx={{ 
              p: 2, 
              bgcolor: '#f5f5f5',
              borderRadius: 1,
              border: '1px solid #e0e0e0'
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                <Box 
                  sx={{ 
                    width: 12, 
                    height: 12, 
                    borderRadius: '50%', 
                    bgcolor: selectedShiftData?.color || '#f0f0f0',
                    border: '1px solid #ddd'
                  }} 
                />
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  Editing: {selectedShiftData?.shift_code} - {selectedShiftData?.name}
                </Typography>
              </Box>
              <Typography variant="caption" color="text.secondary">
                Values below are pre-filled. Change what you need - only changes are saved.
              </Typography>
            </Box>

            {/* Time Fields - Pre-filled */}
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
                Shift Times
              </Typography>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TimePicker
                  label="Start Time"
                  value={startTime}
                  onChange={(newValue) => setStartTime(newValue)}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      helperText: `Shift default: ${originalDefaults.start_time || 'N/A'}`
                    }
                  }}
                />
                <TimePicker
                  label="End Time"
                  value={endTime}
                  onChange={(newValue) => setEndTime(newValue)}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      helperText: `Shift default: ${originalDefaults.end_time || 'N/A'}`
                    }
                  }}
                />
              </Box>
            </Box>

            {/* Breaks - Pre-filled */}
            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                <Typography variant="subtitle2">
                  Breaks ({breaks.length})
                </Typography>
                <Button
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={handleAddBreak}
                  variant="outlined"
                >
                  Add Break
                </Button>
              </Box>

              {breaks.length === 0 ? (
                <Alert severity="info" sx={{ fontSize: '0.875rem' }}>
                  No breaks for this shift
                </Alert>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {breaks.map((break_, index) => (
                    <Box 
                      key={index} 
                      sx={{ 
                        p: 2, 
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 1,
                        bgcolor: '#fafafa',
                        display: 'flex',
                        gap: 2,
                        alignItems: 'flex-start'
                      }}
                    >
                      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <TextField
                            label="Start"
                            type="time"
                            value={break_.start_time}
                            onChange={(e) => handleUpdateBreak(index, 'start_time', e.target.value)}
                            size="small"
                            InputLabelProps={{ shrink: true }}
                            sx={{ flex: 1 }}
                          />
                          <TextField
                            label="End"
                            type="time"
                            value={break_.end_time}
                            onChange={(e) => handleUpdateBreak(index, 'end_time', e.target.value)}
                            size="small"
                            InputLabelProps={{ shrink: true }}
                            sx={{ flex: 1 }}
                          />
                          <FormControl size="small" sx={{ flex: 1 }}>
                            <InputLabel>Type</InputLabel>
                            <Select
                              value={break_.break_type}
                              onChange={(e) => handleUpdateBreak(index, 'break_type', e.target.value)}
                              label="Type"
                            >
                              <MenuItem value="BREAK">Break</MenuItem>
                              <MenuItem value="LUNCH">Lunch</MenuItem>
                              <MenuItem value="TEA">Tea</MenuItem>
                              <MenuItem value="PRAYER">Prayer</MenuItem>
                            </Select>
                          </FormControl>
                        </Box>
                        <TextField
                          label="Description (optional)"
                          value={break_.description || ''}
                          onChange={(e) => handleUpdateBreak(index, 'description', e.target.value)}
                          size="small"
                          fullWidth
                          placeholder="E.g., Lunch break"
                        />
                      </Box>
                      <IconButton 
                        size="small" 
                        onClick={() => handleDeleteBreak(index)}
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  ))}
                </Box>
              )}
            </Box>

            <Divider />

            {/* Notes */}
            <TextField
              label="Notes (optional)"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              multiline
              rows={2}
              fullWidth
              placeholder="E.g., Doctor appointment, Training day, etc."
            />
          </Box>
        </DialogContent>

        <DialogActions sx={{ 
          px: 3, 
          py: 2, 
          borderTop: '1px solid',
          borderColor: 'divider',
          justifyContent: 'space-between'
        }}>
          <Box>
            {hasExistingOverride && (
              <Button
                onClick={handleDelete}
                color="error"
                startIcon={<DeleteIcon />}
                disabled={loading}
              >
                Delete Override
              </Button>
            )}
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button onClick={handleClose} disabled={loading}>
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              variant="contained"
              disabled={loading || !selectedShift}
              sx={{
                bgcolor: '#E4C984',
                color: '#171717',
                '&:hover': { bgcolor: '#d4b974' }
              }}
            >
              {loading ? 'Saving...' : 'Save'}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>
    </LocalizationProvider>
  );
};

export default ShiftOverrideModal;

